import { useEffect, useState } from "react";
import {
  authHeaders,
  handleUnauthorized,
} from "@/lib/auth";

const API_BASE = process.env.REACT_APP_BACKEND_URL || "";

const EMPTY_SETTINGS = {
  eyebrow: "TapToTable Success Center",
  title: "Resources to Help You Grow",
  description:
    "Get support with your TapToTable page or explore restaurant funding options.",
  announcement_enabled: false,
  announcement_title: "",
  announcement_text: "",
  resources: [],
};

export default function AdminSuccessCenter() {
  const [form, setForm] = useState(EMPTY_SETTINGS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");

  useEffect(() => {
    loadSettings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function loadSettings() {
    try {
      setLoading(true);
      setMessage("");

      const res = await fetch(`${API_BASE}/api/success-center`);

      if (!res.ok) {
        setMessage("Could not load Success Center settings.");
        return;
      }

      const data = await res.json();

      setForm({
        ...EMPTY_SETTINGS,
        ...data,
        resources: Array.isArray(data.resources)
          ? data.resources
          : [],
      });
    } catch (err) {
      console.error(err);
      setMessage("Could not load Success Center settings.");
    } finally {
      setLoading(false);
    }
  }

  function updateField(field, value) {
    setForm((prev) => ({
      ...prev,
      [field]: value,
    }));
  }

  function updateResource(index, field, value) {
    setForm((prev) => ({
      ...prev,
      resources: prev.resources.map((resource, resourceIndex) =>
        resourceIndex === index
          ? {
              ...resource,
              [field]: value,
            }
          : resource
      ),
    }));
  }

  function addResource() {
    setForm((prev) => ({
      ...prev,
      resources: [
        ...prev.resources,
        {
          title: "",
          description: "",
          button_text: "",
          url: "",
          enabled: true,
          open_new_tab: true,
        },
      ],
    }));
  }

  function removeResource(index) {
    setForm((prev) => ({
      ...prev,
      resources: prev.resources.filter(
        (_, resourceIndex) => resourceIndex !== index
      ),
    }));
  }

  async function saveSettings(e) {
    e.preventDefault();

    try {
      setSaving(true);
      setMessage("");

      const res = await fetch(`${API_BASE}/api/success-center`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...authHeaders(),
        },
        body: JSON.stringify(form),
      });

      const data = await res.json().catch(() => ({}));

      if (res.status === 401 || res.status === 403) {
        handleUnauthorized();
        return;
      }

      if (!res.ok) {
        setMessage(
          data.detail || "Could not save Success Center settings."
        );
        return;
      }

      setForm({
        ...EMPTY_SETTINGS,
        ...data,
        resources: Array.isArray(data.resources)
          ? data.resources
          : [],
      });

      setMessage("Success Center settings saved.");
    } catch (err) {
      console.error(err);
      setMessage("Could not save Success Center settings.");
    } finally {
      setSaving(false);
    }
  }

  if (loading) {
    return (
      <main className="min-h-screen bg-zinc-950 text-white flex items-center justify-center">
        <p className="text-zinc-400">
          Loading Success Center settings...
        </p>
      </main>
    );
  }

  return (
    <main className="min-h-screen bg-zinc-950 text-white p-6">
      <div className="max-w-4xl mx-auto">
        <h1 className="text-3xl font-bold mb-2">
          Success Center
        </h1>

        <p className="text-zinc-400 mb-8">
          Manage the resources and announcements shown on every
          client dashboard.
        </p>

        <form onSubmit={saveSettings} className="space-y-8">
          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6 space-y-5">
            <div>
              <h2 className="text-xl font-bold">
                Section Content
              </h2>

              <p className="mt-1 text-sm text-zinc-400">
                Control the heading shown above the client resources.
              </p>
            </div>

            <Field
              label="Eyebrow"
              value={form.eyebrow}
              onChange={(value) =>
                updateField("eyebrow", value)
              }
            />

            <Field
              label="Title"
              value={form.title}
              onChange={(value) =>
                updateField("title", value)
              }
            />

            <Field
              label="Description"
              value={form.description}
              onChange={(value) =>
                updateField("description", value)
              }
              textarea
            />
          </section>

          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6 space-y-5">
            <div>
              <h2 className="text-xl font-bold">
                Announcement
              </h2>

              <p className="mt-1 text-sm text-zinc-400">
                Show an optional announcement on every client dashboard.
              </p>
            </div>

            <label className="flex items-center gap-3">
              <input
                type="checkbox"
                checked={form.announcement_enabled}
                onChange={(e) =>
                  updateField(
                    "announcement_enabled",
                    e.target.checked
                  )
                }
                className="h-4 w-4"
              />

              <span className="text-sm text-zinc-300">
                Show announcement
              </span>
            </label>

            <Field
              label="Announcement Title"
              value={form.announcement_title}
              onChange={(value) =>
                updateField("announcement_title", value)
              }
            />

            <Field
              label="Announcement Text"
              value={form.announcement_text}
              onChange={(value) =>
                updateField("announcement_text", value)
              }
              textarea
            />
          </section>

          <section className="rounded-2xl border border-zinc-800 bg-zinc-900 p-6">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h2 className="text-xl font-bold">
                  Resources
                </h2>

                <p className="mt-1 text-sm text-zinc-400">
                  Add, edit, disable, or remove dashboard resource cards.
                </p>
              </div>

              <button
                type="button"
                onClick={addResource}
                className="rounded-xl border border-cyan-400/40 px-4 py-2 text-sm font-semibold text-cyan-300 hover:bg-cyan-400/10"
              >
                Add Resource
              </button>
            </div>

            <div className="mt-6 space-y-5">
              {form.resources.map((resource, index) => (
                <div
                  key={index}
                  className="rounded-xl border border-zinc-800 bg-zinc-950 p-5 space-y-4"
                >
                  <div className="flex items-center justify-between gap-4">
                    <h3 className="font-semibold">
                      Resource {index + 1}
                    </h3>

                    <button
                      type="button"
                      onClick={() => removeResource(index)}
                      className="text-sm font-semibold text-red-400 hover:text-red-300"
                    >
                      Remove
                    </button>
                  </div>

                  <Field
                    label="Resource Title"
                    value={resource.title}
                    onChange={(value) =>
                      updateResource(index, "title", value)
                    }
                  />

                  <Field
                    label="Description"
                    value={resource.description}
                    onChange={(value) =>
                      updateResource(
                        index,
                        "description",
                        value
                      )
                    }
                    textarea
                  />

                  <Field
                    label="Button Text"
                    value={resource.button_text}
                    onChange={(value) =>
                      updateResource(
                        index,
                        "button_text",
                        value
                      )
                    }
                  />

                  <Field
                    label="URL or Email Link"
                    value={resource.url}
                    onChange={(value) =>
                      updateResource(index, "url", value)
                    }
                    placeholder="https://example.com or mailto:name@example.com"
                  />

                  <div className="flex flex-wrap gap-6">
                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={resource.enabled}
                        onChange={(e) =>
                          updateResource(
                            index,
                            "enabled",
                            e.target.checked
                          )
                        }
                        className="h-4 w-4"
                      />

                      <span className="text-sm text-zinc-300">
                        Enabled
                      </span>
                    </label>

                    <label className="flex items-center gap-3">
                      <input
                        type="checkbox"
                        checked={resource.open_new_tab}
                        onChange={(e) =>
                          updateResource(
                            index,
                            "open_new_tab",
                            e.target.checked
                          )
                        }
                        className="h-4 w-4"
                      />

                      <span className="text-sm text-zinc-300">
                        Open in new tab
                      </span>
                    </label>
                  </div>
                </div>
              ))}

              {form.resources.length === 0 && (
                <p className="text-sm text-zinc-500">
                  No resources have been added.
                </p>
              )}
            </div>
          </section>

          <div className="flex flex-wrap items-center gap-4">
            <button
              disabled={saving}
              className="rounded-xl bg-cyan-400 px-6 py-3 font-bold text-zinc-950 disabled:opacity-60"
            >
              {saving
                ? "Saving..."
                : "Save Success Center"}
            </button>

            {message && (
              <p className="text-sm text-zinc-300">
                {message}
              </p>
            )}
          </div>
        </form>
      </div>
    </main>
  );
}

function Field({
  label,
  value,
  onChange,
  textarea = false,
  placeholder = "",
}) {
  return (
    <div>
      <label className="mb-2 block text-sm text-zinc-300">
        {label}
      </label>

      {textarea ? (
        <textarea
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          rows={4}
          placeholder={placeholder}
          className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
        />
      ) : (
        <input
          value={value || ""}
          onChange={(e) => onChange(e.target.value)}
          placeholder={placeholder}
          className="w-full rounded-xl border border-zinc-700 bg-zinc-950 px-4 py-3 text-white focus:border-cyan-400 focus:outline-none"
        />
      )}
    </div>
  );
}
